import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, Send, FileText, Download, Loader2, ShieldCheck, Building2 } from "lucide-react";
import { toast } from "sonner";

type Msg = {
  id: string;
  sender_id: string;
  sender_name: string | null;
  sender_role: "auditor" | "employee" | "admin";
  content: string | null;
  attachment_path: string | null;
  attachment_name: string | null;
  attachment_size: number | null;
  attachment_mime: string | null;
  created_at: string;
};

const fmtBytes = (n?: number | null) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtTime = (iso: string) => {
  // Always show full date + time so the conversation has an unambiguous
  // audit-trail timestamp on every message.
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Shared Audit Room chat — used by both the auditor portal and the employee
 * intranet. Theme is "light" by default (white surface, dark text) but can be
 * forced to "dark" via the prop.
 */
const AuditRoom = ({ theme = "light" }: { theme?: "light" | "dark" }) => {
  const { user, fullName, isAuditor, isEmployee, isAdmin } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [pending, setPending] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const myRole: Msg["sender_role"] = isAuditor ? "auditor" : isAdmin ? "admin" : "employee";

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["audit-room"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("auditor_chat_messages" as any)
        .select("*")
        .order("created_at", { ascending: true })
        .limit(500);
      if (error) throw error;
      return ((data as unknown) || []) as Msg[];
    },
    refetchInterval: 8000,
  });

  // realtime
  useEffect(() => {
    const ch = supabase
      .channel("audit-room-rt")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "auditor_chat_messages" },
        () => qc.invalidateQueries({ queryKey: ["audit-room"] }),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [qc]);

  // signed URL cache
  const [urlCache, setUrlCache] = useState<Record<string, string>>({});
  useEffect(() => {
    const missing = messages.filter((m) => m.attachment_path && !urlCache[m.attachment_path!]);
    if (missing.length === 0) return;
    (async () => {
      const next: Record<string, string> = {};
      for (const m of missing) {
        const { data } = await supabase.storage
          .from("auditor-chat-files")
          .createSignedUrl(m.attachment_path!, 3600);
        if (data?.signedUrl) next[m.attachment_path!] = data.signedUrl;
      }
      if (Object.keys(next).length) setUrlCache((c) => ({ ...c, ...next }));
    })();
  }, [messages, urlCache]);

  // autoscroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    if (!user) return;
    if (!text.trim() && !pending) return;
    setSending(true);
    try {
      let attachment_path: string | null = null;
      let attachment_name: string | null = null;
      let attachment_size: number | null = null;
      let attachment_mime: string | null = null;
      if (pending) {
        const path = `${user.id}/${Date.now()}-${pending.name.replace(/[^\w.\-]/g, "_")}`;
        const { error: upErr } = await supabase.storage
          .from("auditor-chat-files")
          .upload(path, pending, { upsert: false, contentType: pending.type });
        if (upErr) throw upErr;
        attachment_path = path;
        attachment_name = pending.name;
        attachment_size = pending.size;
        attachment_mime = pending.type;
      }
      const { error } = await supabase.from("auditor_chat_messages" as any).insert({
        sender_id: user.id,
        sender_name: fullName || user.email,
        sender_role: myRole,
        content: text.trim() || null,
        attachment_path,
        attachment_name,
        attachment_size,
        attachment_mime,
      });
      if (error) throw error;
      setText("");
      setPending(null);
      if (fileRef.current) fileRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["audit-room"] });
    } catch (e: any) {
      toast.error(e?.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const isDark = theme === "dark";
  const surface = isDark ? "bg-navy-deep text-primary-foreground border-primary-foreground/10" : "bg-white text-slate-900 border-slate-200";
  const subtle = isDark ? "text-primary-foreground/60" : "text-slate-500";

  const grouped = useMemo(() => {
    const out: { date: string; items: Msg[] }[] = [];
    for (const m of messages) {
      const d = new Date(m.created_at).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
      const last = out[out.length - 1];
      if (last && last.date === d) last.items.push(m);
      else out.push({ date: d, items: [m] });
    }
    return out;
  }, [messages]);

  return (
    <div className={`flex flex-col h-[calc(100vh-220px)] min-h-[520px] rounded-2xl border shadow-sm overflow-hidden ${surface}`}>
      {/* Header */}
      <div className={`flex items-center justify-between gap-3 px-5 py-4 border-b ${isDark ? "border-primary-foreground/10 bg-primary-foreground/[0.03]" : "border-slate-200 bg-slate-50/60"}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal/10 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-teal" />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold leading-tight">Audit Room</h2>
            <p className={`text-xs ${subtle}`}>Secure channel — Flag State Auditor ↔ Velocity Offshore</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Live
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-6">
        {isLoading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-teal" />
          </div>
        )}
        {!isLoading && messages.length === 0 && (
          <div className="text-center py-16">
            <ShieldCheck className={`w-10 h-10 mx-auto mb-3 ${isDark ? "text-primary-foreground/30" : "text-slate-300"}`} />
            <p className="font-display text-base font-medium">Open the audit conversation</p>
            <p className={`text-sm mt-1 ${subtle}`}>
              Ask questions, request evidence, and exchange controlled documents in one secure thread.
            </p>
          </div>
        )}

        {grouped.map((g) => (
          <div key={g.date} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className={`flex-1 h-px ${isDark ? "bg-primary-foreground/10" : "bg-slate-200"}`} />
              <span className={`text-[10px] uppercase tracking-[2px] ${subtle}`}>{g.date}</span>
              <div className={`flex-1 h-px ${isDark ? "bg-primary-foreground/10" : "bg-slate-200"}`} />
            </div>
            {g.items.map((m) => {
              const mine = m.sender_id === user?.id;
              const isAuditorMsg = m.sender_role === "auditor";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
                    <div className="flex items-center gap-2 mb-1 text-[11px]">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider text-[9px] ${
                        isAuditorMsg
                          ? "bg-amber-100 text-amber-800"
                          : "bg-teal/15 text-teal"
                      }`}>
                        {isAuditorMsg ? <ShieldCheck className="w-2.5 h-2.5" /> : <Building2 className="w-2.5 h-2.5" />}
                        {isAuditorMsg ? "Auditor" : "Velocity Offshore"}
                      </span>
                      <span className={subtle}>{mine ? "You" : m.sender_name}</span>
                      <span className={subtle}>·</span>
                      <span className={subtle}>{fmtTime(m.created_at)}</span>
                    </div>
                    <div
                      className={`rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
                        mine
                          ? "bg-teal text-white rounded-br-md"
                          : isDark
                          ? "bg-primary-foreground/[0.06] text-primary-foreground rounded-bl-md"
                          : "bg-slate-100 text-slate-900 rounded-bl-md"
                      }`}
                    >
                      {m.content && <p className="whitespace-pre-wrap">{m.content}</p>}
                      {m.attachment_path && (
                        <a
                          href={urlCache[m.attachment_path] || "#"}
                          target="_blank"
                          rel="noreferrer"
                          className={`mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${
                            mine
                              ? "border-white/30 bg-white/10 hover:bg-white/20"
                              : isDark
                              ? "border-primary-foreground/15 bg-primary-foreground/5 hover:bg-primary-foreground/10"
                              : "border-slate-200 bg-white hover:bg-slate-50 text-slate-900"
                          }`}
                        >
                          <FileText className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[200px]">{m.attachment_name}</span>
                          <span className="opacity-60">{fmtBytes(m.attachment_size)}</span>
                          <Download className="w-3 h-3 opacity-70" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Composer */}
      <div className={`border-t px-4 py-3 ${isDark ? "border-primary-foreground/10 bg-primary-foreground/[0.03]" : "border-slate-200 bg-slate-50/60"}`}>
        {pending && (
          <div className={`mb-2 flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${isDark ? "bg-primary-foreground/5" : "bg-white border border-slate-200"}`}>
            <FileText className="w-3.5 h-3.5 text-teal" />
            <span className="truncate flex-1">{pending.name}</span>
            <span className={subtle}>{fmtBytes(pending.size)}</span>
            <button
              onClick={() => { setPending(null); if (fileRef.current) fileRef.current.value = ""; }}
              className={`text-xs ${isDark ? "text-primary-foreground/50" : "text-slate-500"} hover:text-destructive`}
            >
              Remove
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (f.size > 25 * 1024 * 1024) { toast.error("Max attachment size is 25 MB"); return; }
              setPending(f);
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => fileRef.current?.click()}
            className={isDark ? "border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10" : ""}
            title="Attach document"
          >
            <Paperclip className="w-4 h-4" />
          </Button>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={isAuditor ? "Ask the Velocity Offshore team a question…" : "Reply to the auditor…"}
            rows={2}
            className={`flex-1 resize-none ${isDark ? "bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground placeholder:text-primary-foreground/40" : ""}`}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
            }}
          />
          <Button
            onClick={send}
            disabled={sending || (!text.trim() && !pending)}
            className="bg-teal hover:bg-teal/90 text-white"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send</>}
          </Button>
        </div>
        <p className={`mt-1.5 text-[10px] ${subtle}`}>
          Press ⌘/Ctrl + Enter to send · Attachments up to 25 MB · All messages and files are logged for audit trail.
        </p>
      </div>
    </div>
  );
};

export default AuditRoom;
